import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import SellerLayout from './SellerLayout';

/**
 * SellerSmartfarmList.jsx
 *
 * 연결 API
 * GET  /api/seller/smartfarm/list-data
 * POST /api/seller/smartfarm/toggle-active
 * POST /api/seller/smartfarm/delete-process
 *
 * 필요 환경
 * - React Router
 * - axios
 * - Bootstrap 5
 */
const SellerSmartfarmList = () => {
  const navigate = useNavigate();

  const [sites, setSites] = useState([]);
  const [shopName, setShopName] = useState('');
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState('');

  const api = useMemo(() => axios.create({
    withCredentials: true,
  }), []);

  const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  };

  const fetchSites = useCallback(async (query = '') => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/api/seller/smartfarm/list-data', {
        params: query ? { keyword: query } : {},
      });

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '스마트팜 목록을 불러오지 못했습니다.');
      }

      setSites(Array.isArray(data.sites) ? data.sites : []);
      setShopName(data.shop_name || '');
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        '스마트팜 목록을 불러오지 못했습니다.';

      if (status === 401) {
        navigate('/seller/manage', { replace: true });
        return;
      }

      setError(message);
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, [api, navigate]);

  useEffect(() => {
    fetchSites('');
  }, [fetchSites]);

  const handleSearch = (event) => {
    event.preventDefault();

    const value = keyword.trim();
    setSearchKeyword(value);
    fetchSites(value);
  };

  const handleResetSearch = () => {
    setKeyword('');
    setSearchKeyword('');
    fetchSites('');
  };

  const handleToggleActive = async (site) => {
    if (!site?.id || actionLoadingId) return;

    const nextActive = site.is_active === 'Y' ? 'N' : 'Y';
    const actionText = nextActive === 'Y' ? '사용' : '중지';

    if (!window.confirm(`"${site.site_name}" 스마트팜을 ${actionText} 상태로 변경할까요?`)) {
      return;
    }

    setActionLoadingId(site.id);
    setError('');

    try {
      const response = await api.post('/api/seller/smartfarm/toggle-active', {
        id: site.id,
        is_active: nextActive,
      });

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '사용 상태 변경에 실패했습니다.');
      }

      setSites((prev) =>
        prev.map((item) =>
          item.id === site.id
            ? { ...item, is_active: data.is_active || nextActive }
            : item
        )
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.message ||
        '사용 상태 변경에 실패했습니다.'
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (site) => {
    if (!site?.id || actionLoadingId) return;

    const confirmed = window.confirm(
      `"${site.site_name}" 스마트팜을 삭제할까요?\n\n저장된 엔티티와 제어 로그도 함께 삭제될 수 있습니다.`
    );

    if (!confirmed) return;

    setActionLoadingId(site.id);
    setError('');

    try {
      const response = await api.post('/api/seller/smartfarm/delete-process', {
        id: site.id,
      });

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '스마트팜 삭제에 실패했습니다.');
      }

      setSites((prev) => prev.filter((item) => item.id !== site.id));
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.message ||
        '스마트팜 삭제에 실패했습니다.'
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const renderConnectionBadge = (status) => {
    if (status === 'CONNECTED') {
      return (
        <span className="badge rounded-pill text-bg-success">
          연결됨
        </span>
      );
    }

    return (
      <span className="badge rounded-pill text-bg-secondary">
        연결 안 됨
      </span>
    );
  };

  const renderActiveBadge = (isActive) => {
    if (isActive === 'Y') {
      return (
        <span className="badge rounded-pill text-bg-primary">
          사용 중
        </span>
      );
    }

    return (
      <span className="badge rounded-pill text-bg-light border text-secondary">
        사용 중지
      </span>
    );
  };

  return (
    <SellerLayout>
    <div className="container-fluid px-0">
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1 fw-bold">스마트팜 관리</h2>
          <p className="mb-0 text-secondary">
            {shopName ? `${shopName}의 ` : ''}
            회원별 스마트팜과 Home Assistant 연결을 관리합니다.
          </p>
        </div>

        <Link
          to="/seller/smartfarm/add"
          className="btn btn-primary text-nowrap"
        >
          스마트팜 추가
        </Link>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <form
            onSubmit={handleSearch}
            className="row g-2 align-items-center"
          >
            <div className="col-12 col-md">
              <input
                type="search"
                className="form-control"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="농장명, 회원명, 이메일, 전화번호 검색"
                aria-label="스마트팜 검색"
              />
            </div>

            <div className="col-6 col-md-auto">
              <button
                type="submit"
                className="btn btn-dark w-100"
                disabled={loading}
              >
                검색
              </button>
            </div>

            <div className="col-6 col-md-auto">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={handleResetSearch}
                disabled={loading && !searchKeyword}
              >
                초기화
              </button>
            </div>
          </form>
        </div>
      </div>

      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setError('')}
          />
        </div>
      )}

      <div className="card border-0 shadow-sm overflow-hidden">
        <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <strong>
            등록된 스마트팜
          </strong>
          <span className="text-secondary small">
            {loading ? '불러오는 중' : `총 ${sites.length}개`}
          </span>
        </div>

        {loading ? (
          <div className="py-5 text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              aria-label="로딩 중"
            />
            <div className="mt-3 text-secondary">
              스마트팜 목록을 불러오고 있습니다.
            </div>
          </div>
        ) : sites.length === 0 ? (
          <div className="py-5 px-3 text-center">
            <div className="fs-5 fw-semibold mb-2">
              등록된 스마트팜이 없습니다.
            </div>
            <p className="text-secondary mb-3">
              회원을 선택하고 농장의 Home Assistant 주소와 토큰을 등록하세요.
            </p>
            <Link
              to="/seller/smartfarm/add"
              className="btn btn-primary"
            >
              첫 스마트팜 등록
            </Link>
          </div>
        ) : (
          <>
            <div className="table-responsive d-none d-lg-block">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4">농장</th>
                    <th>회원</th>
                    <th>HA 주소</th>
                    <th className="text-center">연결</th>
                    <th className="text-center">기기</th>
                    <th className="text-center">사용 상태</th>
                    <th>수정일</th>
                    <th className="text-end pe-4">관리</th>
                  </tr>
                </thead>

                <tbody>
                  {sites.map((site) => {
                    const busy = actionLoadingId === site.id;

                    return (
                      <tr key={site.id}>
                        <td className="ps-4">
                          <div className="fw-semibold">
                            {site.site_name || '-'}
                          </div>
                          <div className="small text-secondary">
                            ID {site.id}
                          </div>
                        </td>

                        <td>
                          <div className="fw-medium">
                            {site.member_nickname || '이름 없음'}
                          </div>
                          <div className="small text-secondary">
                            {site.member_email || site.member_phone || '-'}
                          </div>
                        </td>

                        <td>
                          <div
                            className="text-truncate"
                            style={{ maxWidth: 260 }}
                            title={site.ha_base_url || ''}
                          >
                            {site.ha_base_url || '-'}
                          </div>
                        </td>

                        <td className="text-center">
                          {renderConnectionBadge(site.connection_status)}
                        </td>

                        <td className="text-center">
                          <span className="fw-semibold">
                            {Number(site.entity_count || 0)}
                          </span>
                          <span className="text-secondary ms-1">개</span>
                        </td>

                        <td className="text-center">
                          {renderActiveBadge(site.is_active)}
                        </td>

                        <td className="text-secondary small">
                          {formatDateTime(site.updated_at)}
                        </td>

                        <td className="text-end pe-4">
                          <div className="d-inline-flex flex-wrap justify-content-end gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                navigate(`/seller/smartfarm/preview?site_id=${site.id}`)
                              }
                              disabled={busy}
                            >
                              사용자화면 미리보기
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              onClick={() =>
                                navigate(`/seller/smartfarm/3d/preview?site_id=${site.id}`)
                              }
                              disabled={busy}
                            >
                              3D 사용자화면 미리보기
                            </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() =>
                              navigate(`/seller/smartfarm/esp32?site_id=${site.id}`)
                            }
                            disabled={busy}
                          >
                            수동접속
                          </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                navigate(`/seller/smartfarm/edit?id=${site.id}`)
                              }
                              disabled={busy}
                            >
                              수정
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm ${
                                site.is_active === 'Y'
                                  ? 'btn-outline-warning'
                                  : 'btn-outline-primary'
                              }`}
                              onClick={() => handleToggleActive(site)}
                              disabled={busy}
                            >
                              {busy
                                ? '처리 중'
                                : site.is_active === 'Y'
                                  ? '사용 중지'
                                  : '사용'}
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(site)}
                              disabled={busy}
                            >
                              삭제
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-lg-none">
              {sites.map((site) => {
                const busy = actionLoadingId === site.id;

                return (
                  <div
                    key={site.id}
                    className="border-top p-3"
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <div className="min-w-0">
                        <div className="fw-bold fs-5 text-break">
                          {site.site_name || '-'}
                        </div>
                        <div className="small text-secondary">
                          {site.member_nickname || '이름 없음'}
                          {site.member_email
                            ? ` · ${site.member_email}`
                            : ''}
                        </div>
                      </div>

                      <div className="d-flex flex-column align-items-end gap-1">
                        {renderConnectionBadge(site.connection_status)}
                        {renderActiveBadge(site.is_active)}
                      </div>
                    </div>

                    <dl className="row small mb-3">
                      <dt className="col-4 text-secondary fw-normal">
                        HA 주소
                      </dt>
                      <dd className="col-8 text-break mb-2">
                        {site.ha_base_url || '-'}
                      </dd>

                      <dt className="col-4 text-secondary fw-normal">
                        등록 기기
                      </dt>
                      <dd className="col-8 mb-2">
                        {Number(site.entity_count || 0)}개
                      </dd>

                      <dt className="col-4 text-secondary fw-normal">
                        수정일
                      </dt>
                      <dd className="col-8 mb-0">
                        {formatDateTime(site.updated_at)}
                      </dd>
                    </dl>

                    <div className="row g-2">
                      <div className="col-12">
                        <button
                          type="button"
                          className="btn btn-outline-primary w-100"
                          onClick={() =>
                            navigate(`/seller/smartfarm/preview?site_id=${site.id}`)
                          }
                          disabled={busy}
                        >
                          사용자화면 미리보기
                        </button>
                      </div>

                      <div className="col-12">
                        <button
                          type="button"
                          className="btn btn-outline-dark w-100"
                          onClick={() =>
                            navigate(`/seller/smartfarm/3d/preview?site_id=${site.id}`)
                          }
                          disabled={busy}
                        >
                          3D 사용자화면 미리보기
                        </button>
                      </div>

                      <div className="col-12">
                        <button
                          type="button"
                          className="btn btn-outline-success w-100"
                          onClick={() =>
                            navigate(`/seller/smartfarm/esp32?site_id=${site.id}`)
                          }
                          disabled={busy}
                        >
                          수동접속
                        </button>
                      </div>

                      <div className="col-4">
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100"
                          onClick={() =>
                            navigate(`/seller/smartfarm/edit?id=${site.id}`)
                          }
                          disabled={busy}
                        >
                          수정
                        </button>
                      </div>

                      <div className="col-4">
                        <button
                          type="button"
                          className={`btn w-100 ${
                            site.is_active === 'Y'
                              ? 'btn-outline-warning'
                              : 'btn-outline-primary'
                          }`}
                          onClick={() => handleToggleActive(site)}
                          disabled={busy}
                        >
                          {busy
                            ? '처리 중'
                            : site.is_active === 'Y'
                              ? '중지'
                              : '사용'}
                        </button>
                      </div>

                      <div className="col-4">
                        <button
                          type="button"
                          className="btn btn-outline-danger w-100"
                          onClick={() => handleDelete(site)}
                          disabled={busy}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
    </SellerLayout>
  );
};

export default SellerSmartfarmList;
